// The Queuing tab's schematic, left to right: service groups → segmentation →
// queues → work profiles. HTML cards in four grid columns; the connectors are drawn
// from the cards' measured places.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Button, Chip } from '@heroui/react'
import { buildGraph, highlight, type NodeId } from '../../lib/profilesGraph'
import { nameIn, type StaffKey } from '../../lib/staffText'
import type { OperationDoc, ProfileProblem, Service } from '../../lib/types'
import { useSize } from '../../reports/insightVisuals'
import { Icon } from '../../ui/icons'
import { useStaffText } from '../../ui/StaffText'
import { Connectors, type Anchor } from './Connectors'

export type Open = { kind: 'group' | 'segment' | 'queue' | 'work'; id: string; groupId?: string }
type AddKind = 'group' | 'queue' | 'work' | 'segment'

const SERVICE_LINES = 4

export function Schematic({ doc, services, pins, readOnly, onOpen, onAdd }: {
  doc: OperationDoc
  services: Service[]
  pins: Map<string, ProfileProblem[]>
  readOnly: boolean
  onOpen: (o: Open) => void
  onAdd: (kind: AddKind, groupId?: string) => void
}) {
  const { lang, say } = useStaffText()
  const graph = useMemo(() => buildGraph(doc), [doc])
  const [hover, setHover] = useState<NodeId | null>(null)
  const lit = useMemo(() => (hover ? highlight(graph, hover) : null), [graph, hover])

  // Anchors: every node's element, measured against the grid after each layout.
  const els = useRef(new Map<NodeId, HTMLElement>())
  const refs = useRef(new Map<NodeId, (el: HTMLElement | null) => void>())
  const anchor = useCallback((id: NodeId) => {
    let ref = refs.current.get(id)
    if (!ref) {
      ref = (el) => { if (el) els.current.set(id, el); else els.current.delete(id) }
      refs.current.set(id, ref)
    }
    return ref
  }, [])
  const [setSized, size] = useSize<HTMLDivElement>()
  const grid = useRef<HTMLDivElement | null>(null)
  const setGrid = useCallback((el: HTMLDivElement | null) => { grid.current = el; setSized(el) }, [setSized])
  const [moved, setMoved] = useState(0)
  useEffect(() => {
    const cols = grid.current?.querySelectorAll('.pf-col')
    if (!cols || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => setMoved((n) => n + 1))
    cols.forEach((c) => ro.observe(c))
    return () => ro.disconnect()
  }, [])
  const [layout, setLayout] = useState<{ anchors: Map<NodeId, Anchor>; w: number; h: number }>({ anchors: new Map(), w: 0, h: 0 })
  useLayoutEffect(() => {
    const g = grid.current
    if (!g) return
    const base = g.getBoundingClientRect()
    const anchors = new Map<NodeId, Anchor>()
    for (const [id, el] of els.current) {
      const r = el.getBoundingClientRect()
      anchors.set(id, { l: r.left - base.left, r: r.right - base.left, y: r.top - base.top + r.height / 2, h: r.height })
    }
    setLayout({ anchors, w: Math.ceil(base.width), h: Math.ceil(base.height) })
  }, [doc, size, moved, lang, pins])

  const state = (id: NodeId) => ({
    'data-node': id,
    'data-lit': lit?.nodes.has(id) || undefined,
    'data-dim': (lit && !lit.nodes.has(id)) || undefined,
  })
  const hoverOn = (id: NodeId) => ({
    onPointerEnter: (e: React.PointerEvent) => { if (e.pointerType !== 'touch') setHover(id) },
    onPointerLeave: () => setHover(null),
    onFocus: () => setHover(id),
    onBlur: () => setHover(null),
  })
  const node = (id: NodeId, open: Open) => ({ ref: anchor(id), ...state(id), ...hoverOn(id), onClick: () => onOpen(open) })

  const pinned = (problems: ProfileProblem[] | undefined) => {
    if (!problems?.length) return {}
    return problems.some((p) => !p.warning) ? { 'data-bad': true } : { 'data-warn': true }
  }
  const badge = (problems: ProfileProblem[] | undefined) =>
    problems?.length ? (
      <span className="pf-badge" title={say('profiles.nProblems', { n: problems.length })}>
        {problems.length}
      </span>
    ) : null
  const add = (kind: AddKind, label: StaffKey, groupId?: string) =>
    readOnly ? null : (
      <Button variant="outline" size="sm" className="pf-add" onPress={() => onAdd(kind, groupId)}>
        <Icon name="plus" size={14} /> {say(label)}
      </Button>
    )

  const serviceName = new Map(services.map((s) => [s.id, nameIn(s.name, lang, s.code)]))
  const queue = new Map(doc.queues.map((q) => [q.id, q]))
  const letter = (id: string) => queue.get(id)?.letter ?? '—'
  const byId = <T extends { id: string }>(list: T[]) => new Map(list.map((x) => [x.id, x]))
  const groups = byId(doc.groups)
  const work = byId(doc.workProfiles)
  const sid = (id: NodeId) => id.slice(2)

  const column = (title: StaffKey, children: ReactNode) => (
    <section className="pf-col" aria-label={say(title)}>
      <h3 className="pf-col__head">{say(title)}</h3>
      {children}
    </section>
  )

  return (
    <div className="pf-frame">
      <div className="pf-schematic" ref={setGrid} data-hovering={lit ? true : undefined}>
        <Connectors edges={graph.edges} anchors={layout.anchors} width={layout.w} height={layout.h} lit={lit?.edges ?? null} />

        {column('profiles.col.groups', (
          <>
            {graph.columns.group.map((id) => {
              const g = groups.get(sid(id))!
              const shown = g.serviceIds.slice(0, SERVICE_LINES)
              const more = g.serviceIds.length - shown.length
              return (
                <button key={id} type="button" className="pf-card" {...node(id, { kind: 'group', id: g.id })} {...pinned(pins.get(g.id))}>
                  <span className="pf-card__name">{g.name}</span>
                  {shown.map((s) => (
                    <span key={s} className="pf-card__line">• {serviceName.get(s) ?? s}</span>
                  ))}
                  {more > 0 && <span className="pf-card__line pf-card__more">+{more}</span>}
                  {badge(pins.get(g.id))}
                </button>
              )
            })}
            {doc.groups.length === 0 && <p className="pf-empty">{say('profiles.startGroups')}</p>}
            {add('group', 'profiles.add.group')}
          </>
        ))}

        {column('profiles.segmentation', graph.columns.segmentation.map((id) => {
          const g = groups.get(sid(id))!
          const defaultProblems = pins.get(g.id)?.filter((p) => p.path.endsWith('.defaultQueueId'))
          return (
            <div key={id} className="pf-card pf-seg" ref={anchor(id)} data-lit={state(id)['data-lit']} data-dim={state(id)['data-dim']}>
              <button type="button" className="pf-seg__head" data-node={id} {...hoverOn(id)} onClick={() => onOpen({ kind: 'group', id: g.id })}>
                {g.name}
              </button>
              <ol className="pf-seg__rows">
                {g.segments.map((s, i) => {
                  const rid = `r:${s.id}`
                  return (
                    <li key={rid}>
                      <button type="button" className="pf-srow" {...node(rid, { kind: 'segment', id: s.id, groupId: g.id })} {...pinned(pins.get(s.id))}>
                        <span className="pf-srow__n">{i + 1}</span>
                        <span className="pf-srow__name">{s.name}</span>
                        <span className="pf-srow__to">→ {letter(s.queueId)}</span>
                        {badge(pins.get(s.id))}
                      </button>
                    </li>
                  )
                })}
                <li>
                  <button type="button" className="pf-srow pf-srow--default" {...node(`d:${g.id}`, { kind: 'group', id: g.id })} {...pinned(defaultProblems)}>
                    <span className="pf-srow__n" />
                    <span className="pf-srow__name">{say('profiles.default')}</span>
                    <span className="pf-srow__to">→ {letter(g.defaultQueueId)}</span>
                    {badge(defaultProblems)}
                  </button>
                </li>
              </ol>
              {add('segment', 'profiles.add.segment', g.id)}
            </div>
          )
        }))}

        {column('profiles.col.queues', (
          <>
            {graph.columns.queue.map((id) => {
              const q = queue.get(sid(id))!
              return (
                <button key={id} type="button" className="pf-card pf-queue" {...node(id, { kind: 'queue', id: q.id })} {...pinned(pins.get(q.id))}>
                  <span className="stub">{q.letter}</span>
                  <span className="pf-queue__body">
                    <span className="pf-card__name">{nameIn(q.name, lang, q.code)}</span>
                    <span className="pf-card__line">
                      <span className="pf-code">{q.letter}</span> · <span className="pf-nb">{q.firstNumber}–{q.lastNumber}</span>
                    </span>
                    <span className="pf-card__line">{say('profiles.targetMin', { n: Math.round(q.serviceLevelSec / 60) })}</span>
                    {q.type === 'appointment' && (
                      <Chip size="sm" variant="secondary" className="pf-tag">
                        {say('profiles.booked')}
                      </Chip>
                    )}
                  </span>
                  {badge(pins.get(q.id))}
                </button>
              )
            })}
            {add('queue', 'profiles.add.queue')}
          </>
        ))}

        {column('profiles.col.work', (
          <>
            {graph.columns.work.map((id) => {
              const w = work.get(sid(id))!
              return (
                <button key={id} type="button" className="pf-card" {...node(id, { kind: 'work', id: w.id })} {...pinned(pins.get(w.id))}>
                  <span className="pf-card__name">{w.name}</span>
                  {(w.queueIds.length > 0 || w.rules.length > 1) && (
                    <span className="pf-work__calls">
                      {w.queueIds.map((q, i) => (
                        <span key={`${q}-${i}`} className="stub stub--sm">{letter(q)}</span>
                      ))}
                      {w.rules.length > 1 && (
                        <Chip size="sm" variant="secondary" className="pf-tag">
                          {say('profiles.nRules', { n: w.rules.length })}
                        </Chip>
                      )}
                    </span>
                  )}
                  {badge(pins.get(w.id))}
                </button>
              )
            })}
            {add('work', 'profiles.add.work')}
          </>
        ))}
      </div>
    </div>
  )
}
